import { Box } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { dotLegalExpandedNavIcon } from "@common/icons";
import { useCatalogPaneStyles } from "@common/components/catalogPane/CatalogPane.styles";
import { INavigationMenuSettingsDialogProps, useNavigationMenuSettingsDialogHook } from "./NavigationMenuSettingsDialog.hooks";
import { useNavigationMenuSettingsDialogStyles } from "./NavigationMenuSettingsDialog.styles";

function NavigationMenuSettingsDialog(props: INavigationMenuSettingsDialogProps) {
    const styles = useNavigationMenuSettingsDialogStyles();
    const catalogStyles = useCatalogPaneStyles();
    const { translateString } = useTranslation();
    const { menuItemSettings, previewItems, visibleCount, totalCount, allShown, onShownChange, onToggleAll, onSave, inProgress } =
        useNavigationMenuSettingsDialogHook(props);

    return (
        <DotLegalDialog
            open
            size="lg"
            fullHeight
            header={translateString("navigationMenuSettings")}
            buttonOkText={translateString("save")}
            onOkClick={onSave}
            inProgress={inProgress}
            onDialogClose={props.onDialogClose}
        >
            <Box sx={styles.content}>
                <Box sx={styles.description}>
                    <DotLegalHeader headerStyle="small" fontWeight={400} center>
                        {translateString("navigationMenuSettingsDescription")}
                    </DotLegalHeader>
                </Box>

                <Box sx={styles.grid}>
                    <Box sx={styles.settingsColumn}>
                        <Box sx={styles.summaryRow}>
                            <Box sx={styles.summaryPill}>
                                <CheckBoxIcon sx={styles.summaryCheck} />
                                {translateString("navigationMenuVisibleCount", { visible: visibleCount, total: totalCount })}
                            </Box>
                            <Box sx={catalogStyles.chooseAllLink} onClick={onToggleAll}>
                                {translateString(allShown ? "chooseNone" : "chooseAll")}
                            </Box>
                        </Box>

                        <Box sx={styles.list}>
                            {menuItemSettings.map((setting) => (
                                <Box key={setting.item} sx={styles.itemBlock}>
                                    <DotLegalCheckbox
                                        checked={setting.shown}
                                        label={setting.label}
                                        fontSize="m"
                                        onChange={(shown) => onShownChange(setting.item, shown)}
                                    />
                                    {setting.subItems.length > 0 && (
                                        <Box sx={styles.subItemList}>
                                            {setting.subItems.map((subItem) => (
                                                <DotLegalCheckbox
                                                    key={subItem.item}
                                                    checked={subItem.shown}
                                                    label={subItem.label}
                                                    fontSize="m"
                                                    onChange={(shown) => onShownChange(subItem.item, shown)}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={styles.previewColumn}>
                        <Box sx={styles.blobTopRight} />
                        <Box sx={styles.blobBottomLeft} />
                        <Box sx={styles.previewContent}>
                            <Box sx={styles.previewLabel}>{translateString("preview")}</Box>
                            <Box sx={styles.previewCard}>
                                <Box sx={styles.previewLogo}>{dotLegalExpandedNavIcon}</Box>
                                <Box sx={styles.previewList}>
                                    {previewItems.map((previewItem) => (
                                        <Box key={previewItem.item}>
                                            <Box sx={styles.previewItem}>
                                                <Box sx={styles.previewDot} />
                                                {previewItem.label}
                                            </Box>
                                            {previewItem.subItems.map((subItem) => (
                                                <Box key={subItem.item} sx={styles.previewSubItem}>
                                                    <Box sx={styles.previewSubItemDot} />
                                                    {subItem.label}
                                                </Box>
                                            ))}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default NavigationMenuSettingsDialog;
